/**
 * The checkout's Delivery section, headless (order to invoice doc 104 §9.1, §9.3), for the portal checkout and the
 * staff basket alike: what the person picked for each delivery, turned into the choices the preview and the
 * checkout send, and the words for a delivery with no price.
 *
 * The prices, levels, arrival dates, totals and the not-yet-priced and card sentences are the server's; nothing is
 * priced or decided here. A pick is only what the person did: a level, to collect (and where), who receives it, and
 * for staff an agreed price with its reason.
 */

import type { StaffDeliveryChoice } from "../data/basket-types";
import type {
  CustomerDeliveryChoice,
  CustomerDeliveryServiceLevel,
} from "../data/customer-basket-types";
import { penceOfPounds } from "./delivery-selection";
import { formatCount, formatQuotedPrice } from "./formatters";

/** A delivery with no price yet, wherever a price would show (doc 104 §12): never £0. */
export const NOT_YET_PRICED = "Not yet priced";
/** The totals' delivery figure while a delivery is not yet priced: the totals exclude it (doc 104 §12). */
export const DELIVERY_NOT_YET_PRICED = "Delivery not yet priced";

/** A price ex VAT, or "Not yet priced" for a delivery with none (a null price is never drawn as £0). */
export function formatPriceOrNotYetPriced(pence: number | null): string {
  return pence === null ? NOT_YET_PRICED : formatQuotedPrice(pence);
}

// ── The words for a delivery, the same wherever it is read ───────────────

/** Where a delivery goes: "Acme Leeds HQ, LS1 2AB"; null when no destination is known. */
export function deliveryDestinationText(delivery: {
  destination_name: string | null;
  destination_postcode: string | null;
}): string | null {
  if (!delivery.destination_name) return null;
  return delivery.destination_postcode
    ? `${delivery.destination_name}, ${delivery.destination_postcode}`
    : delivery.destination_name;
}

/** What a delivery carries: "Leaflets, A5 · 5,000, Banners · 2". */
export function deliveryLinesText(
  lines: ReadonlyArray<{ title: string; quantity: number }>,
): string {
  return lines.map((l) => `${l.title} · ${formatCount(l.quantity)}`).join(", ");
}

/** A collection point: "Exeter - Oak House, EX2 7NU". */
export function collectionPointText(point: {
  name: string;
  postcode: string | null;
}): string {
  return point.postcode ? `${point.name}, ${point.postcode}` : point.name;
}

/**
 * A delivery in its supplier's price, as staff read it (doc 104 §12): "Next day · PrintCo delivers", with only what is
 * known, and "Included" when neither the level nor the supplier is.
 */
export function includedDeliveryReading(
  levelLabel: string | null,
  supplier: string | null,
): string {
  const parts = [levelLabel, supplier ? `${supplier} delivers` : null].filter(
    Boolean,
  );
  return parts.length > 0 ? parts.join(" · ") : "Included";
}

/**
 * Where a basket line goes, in a few words: its address's name, or, split
 * across several addresses (order to invoice doc 106 §3), how many. Null
 * while it goes nowhere yet.
 */
export function deliveryAddressReading(
  addressName: string | null,
  addressCount: number,
): string | null {
  if (addressCount > 1) return `${addressCount} addresses`;
  return addressName;
}

// ── What the person did ──────────────────────────────────────────────────

const CHOOSABLE_SERVICE_LEVELS: readonly CustomerDeliveryServiceLevel[] = [
  "next_day",
  "pre_noon",
  "pre_1030",
  "pallet",
  "northern_ireland",
];

/** A level a person may choose (the server's level lists are typed as plain strings; `collection` is not one). */
export function isChoosableServiceLevel(
  level: string,
): level is CustomerDeliveryServiceLevel {
  return (CHOOSABLE_SERVICE_LEVELS as readonly string[]).includes(level);
}

/** Who receives (or collects) a delivery, as the person typed it. */
export interface DeliveryRecipient {
  name: string;
  phone: string;
  email: string;
}

/** Staff's agreed price for a delivery, as typed: pounds, and why. */
export interface AgreedDeliveryPrice {
  price: string;
  reason: string;
}

/** What the person did to one delivery. A field left null keeps what the server chose. */
export interface DeliveryPick {
  serviceLevel: CustomerDeliveryServiceLevel | null;
  isCollected: boolean;
  /** Where to collect from; null with one point (it is that one) or before the person chose one of several. */
  collectionPointId: string | null;
  /** Who receives it when it is delivered ("Change"); null: as the server has it (the address's recipient). */
  recipient: DeliveryRecipient | null;
  /** Who is collecting it, when collected (no email asked); null: as the server has it (the order's contact). */
  collector: Omit<DeliveryRecipient, "email"> | null;
  /** Staff only: the courier picked for the level; null: the level's cheapest. */
  courierSupplierId: string | null;
  /** Staff only: an agreed price instead of a level; null for none. */
  agreed: AgreedDeliveryPrice | null;
}

export const NO_PICK: DeliveryPick = {
  serviceLevel: null,
  isCollected: false,
  collectionPointId: null,
  recipient: null,
  collector: null,
  courierSupplierId: null,
  agreed: null,
};

/** The point a collected delivery goes to: the one chosen, or the only one there is. */
export function collectionPointOf(
  pick: DeliveryPick,
  points: ReadonlyArray<{ id: string }>,
): string | null {
  if (pick.collectionPointId) return pick.collectionPointId;
  return points.length === 1 ? points[0]!.id : null;
}

/** An agreed price is whole when it is a price and has its reason. */
function wholeAgreedPrice(
  agreed: AgreedDeliveryPrice | null,
): { pence: number; reason: string } | null {
  if (!agreed) return null;
  const pence = penceOfPounds(agreed.price);
  const reason = agreed.reason.trim();
  return pence !== null && reason ? { pence, reason } : null;
}

/**
 * True while "Agreed price" is chosen without a price or without its reason: the server refuses a half-entered
 * pair, so it is not sent. The words for what is missing are the server's.
 */
export function isAgreedPriceUnfinished(pick: DeliveryPick): boolean {
  return pick.agreed !== null && wholeAgreedPrice(pick.agreed) === null;
}

function blankToNull(value: string): string | null {
  return value.trim() || null;
}

/** What a pick comes to once the points are known: whether it collects, from where, and who. */
function settle(
  pick: DeliveryPick,
  points: ReadonlyArray<{ id: string }>,
  purpose: "preview" | "place",
) {
  const pointId = pick.isCollected ? collectionPointOf(pick, points) : null;
  const isCollected =
    pick.isCollected && (pointId !== null || purpose === "place");
  const who = isCollected
    ? pick.collector && { ...pick.collector, email: "" }
    : pick.recipient;
  return {
    isCollected,
    pointId,
    recipient: who && {
      recipient_name: blankToNull(who.name),
      recipient_phone: blankToNull(who.phone),
      recipient_email: blankToNull(who.email),
    },
  };
}

/** What the customer's Delivery section showed for a delivery: its ref, how it stands and its price. */
export interface PreviewedCustomerDelivery {
  delivery_ref: string;
  status: string;
  price_pence: number | null;
}

/**
 * The customer's choices to send. A preview sends one per delivery the person changed, and leaves out a collection
 * whose point is not chosen yet (it keeps the delivery as it was until there is somewhere to collect from). Place order
 * sends it, so the server says what is missing in its own words, and sends one choice per previewed delivery (an
 * `included` one has nothing to choose), each with the price the section showed for it, so the server refuses with
 * `prices_changed` when it is no longer that.
 */
export function deliveryChoicesOf(
  picks: Record<string, DeliveryPick>,
  points: ReadonlyArray<{ id: string }>,
  purpose: "preview",
): CustomerDeliveryChoice[];
export function deliveryChoicesOf(
  picks: Record<string, DeliveryPick>,
  points: ReadonlyArray<{ id: string }>,
  purpose: "place",
  previewed: ReadonlyArray<PreviewedCustomerDelivery>,
): CustomerDeliveryChoice[];
export function deliveryChoicesOf(
  picks: Record<string, DeliveryPick>,
  points: ReadonlyArray<{ id: string }>,
  purpose: "preview" | "place",
  previewed: ReadonlyArray<PreviewedCustomerDelivery> = [],
): CustomerDeliveryChoice[] {
  const choiceOf = (
    deliveryRef: string,
    pick: DeliveryPick,
  ): CustomerDeliveryChoice => {
    const settled = settle(pick, points, purpose);
    return {
      delivery_ref: deliveryRef,
      collect: settled.isCollected,
      ...(settled.isCollected
        ? { collection_point_id: settled.pointId }
        : pick.serviceLevel
          ? { service_level: pick.serviceLevel }
          : {}),
      ...settled.recipient,
    };
  };
  if (purpose === "preview")
    return Object.entries(picks).map(([ref, pick]) => choiceOf(ref, pick));
  return previewed
    .filter((d) => d.status !== "included")
    .map((d) => ({
      ...choiceOf(d.delivery_ref, picks[d.delivery_ref] ?? NO_PICK),
      shown: { price_pence: d.price_pence },
    }));
}

/** What a previewed delivery adds to a staff checkout: the price it showed. */
export interface PreviewedStaffDelivery {
  delivery_ref: string;
  delivery: { status: string; delivery_price_pence?: number | null };
}

function staffChoiceOf(
  deliveryRef: string,
  pick: DeliveryPick,
  points: ReadonlyArray<{ id: string }>,
  purpose: "preview" | "place",
): StaffDeliveryChoice {
  const settled = settle(pick, points, purpose);
  const agreed = wholeAgreedPrice(pick.agreed);
  const recipient = settled.recipient && {
    recipient_name: settled.recipient.recipient_name,
    recipient_phone: settled.recipient.recipient_phone,
  };
  return {
    delivery_ref: deliveryRef,
    collect: settled.isCollected,
    ...(settled.isCollected
      ? { collection_point_address_id: settled.pointId }
      : agreed
        ? {
            agreed_delivery_price_pence: agreed.pence,
            agreed_delivery_reason: agreed.reason,
          }
        : pick.serviceLevel
          ? {
              service_level: pick.serviceLevel,
              courier_supplier_id: pick.courierSupplierId,
            }
          : {}),
    ...recipient,
  };
}

/**
 * Staff's choices to send. A preview sends one per delivery the person changed. Place order, Send quote and Add to
 * order send one per previewed delivery (an `included` one has nothing to choose), each with the price the section
 * showed for it, so the server refuses with `prices_changed` when it is no longer that.
 */
export function staffDeliveryChoicesOf(
  picks: Record<string, DeliveryPick>,
  previewed: ReadonlyArray<PreviewedStaffDelivery>,
  points: ReadonlyArray<{ id: string }>,
  purpose: "preview" | "place",
): StaffDeliveryChoice[] {
  if (purpose === "preview")
    return Object.entries(picks).map(([ref, pick]) =>
      staffChoiceOf(ref, pick, points, purpose),
    );
  return previewed
    .filter((d) => d.delivery.status !== "included")
    .map((d) => ({
      ...staffChoiceOf(
        d.delivery_ref,
        picks[d.delivery_ref] ?? NO_PICK,
        points,
        purpose,
      ),
      shown: { delivery_price_pence: d.delivery.delivery_price_pence ?? null },
    }));
}
